// regras/interpretar.js — transforma uma frase falada em dados de contrato.
//
// O professor diz: "Zé, terça e quinta ao meio-dia, cobro 150 a aula, pago 30 de quadra,
// pacote de 8, já fez 3 e tá pago" — e isto devolve os campos preenchidos.
//
// Por que sem IA nesta fase: o vocabulário do professor é pequeno e muito repetitivo
// (dias, horas, valores, pacote, pago/não pago). Regras explícitas acertam quase sempre,
// custam zero, funcionam sem internet e são auditáveis — dá para ver por que entendeu
// cada coisa. Quando a camada de WhatsApp chegar, um modelo de linguagem substitui esta
// função sem mexer em mais nada: a saída é o mesmo objeto que criarAlunoComContrato espera.

const DIAS_MAP = [
  [/\b(domingos?|dom)\b/i, 0],
  [/\b(segundas?[-\s]?(feiras?)?|seg)\b/i, 1],
  [/\b(ter(ç|c)as?[-\s]?(feiras?)?|ter)\b/i, 2],
  [/\b(quartas?[-\s]?(feiras?)?|qua)\b/i, 3],
  [/\b(quintas?[-\s]?(feiras?)?|qui)\b/i, 4],
  [/\b(sextas?[-\s]?(feiras?)?|sex)\b/i, 5],
  [/\b(s(á|a)bados?|sab)\b/i, 6],
];

const NUM_PALAVRA = {
  um: 1, uma: 1, dois: 2, duas: 2, tres: 3, quatro: 4, cinco: 5, seis: 6, sete: 7,
  oito: 8, nove: 9, dez: 10, onze: 11, doze: 12, treze: 13, quatorze: 14, catorze: 14,
  quinze: 15, dezesseis: 16, dezessete: 17, dezoito: 18, dezenove: 19, vinte: 20,
};

function so(txt) {
  return String(txt || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')   // tira acentos para comparar
    .toLowerCase();
}

function numeroPerto(txt, regex) {
  const m = String(txt).match(regex);
  if (!m) return null;
  const bruto = (m[1] || '').replace(/\./g, '').replace(',', '.');
  const n = Number(bruto);
  if (!isNaN(n) && n > 0) return n;
  const palavra = NUM_PALAVRA[so(m[1])];
  return palavra || null;
}

// "às 7", "7 da manhã", "meio-dia", "19:30", "7h30"
export function extrairHora(txt) {
  const t = so(txt);
  if (/meio[\s-]?dia/.test(t)) return '12:00';
  if (/meia[\s-]?noite/.test(t)) return '00:00';

  const m = t.match(/(?:as|às|a)\s*(\d{1,2})(?:[:h](\d{2}))?|\b(\d{1,2})[:h](\d{2})\b|\b(\d{1,2})\s*h\b/);
  if (!m) return null;
  let h = Number(m[1] || m[3] || m[5]);
  const min = m[2] || m[4] || '00';
  if (isNaN(h) || h > 23) return null;

  // "7 da noite" → 19h · "7 da manhã" fica 7h
  if (/(da|de)\s*(noite|tarde)/.test(t) && h < 12) h += 12;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

export function extrairDias(txt) {
  const dias = [];
  for (const [re, n] of DIAS_MAP) if (re.test(txt)) dias.push(n);
  return [...new Set(dias)].sort();
}

// "terça ao meio-dia e quinta às 19h" — cada dia com o seu horário.
// Corta a frase em pedaços que começam em cada dia citado e procura uma hora dentro
// do pedaço. Se só apareceu um horário na frase inteira ("terça e quinta ao meio-dia"),
// devolve null: quem chama usa o horário único e a vida segue simples.
export function extrairHorarios(txt) {
  const t = String(txt || '');
  const marcas = [];
  for (const [re, n] of DIAS_MAP) {
    const busca = new RegExp(re.source, 'gi');
    let m;
    while ((m = busca.exec(t)) !== null) marcas.push({ dia: n, at: m.index });
  }
  if (marcas.length < 2) return null;
  marcas.sort((a, b) => a.at - b.at);

  const mapa = {};
  marcas.forEach((mk, i) => {
    const fim = i + 1 < marcas.length ? marcas[i + 1].at : t.length;
    const h = extrairHora(t.slice(mk.at, fim));
    if (h && mapa[mk.dia] === undefined) mapa[mk.dia] = h;
  });

  const horas = Object.values(mapa);
  if (horas.length < 2 || new Set(horas).size < 2) return null;
  return mapa;
}

// "3 vezes por semana", "3x na semana"
export function extrairFrequencia(txt) {
  return numeroPerto(txt, /(\d+|um|uma|dois|duas|tr[êe]s|quatro|cinco)\s*(?:x|vezes?)\s*(?:por|na|a)?\s*semana/i);
}

// "cobro 150 a aula", "150 por aula", "a aula é 150", "R$ 150"
export function extrairValorAula(txt) {
  return numeroPerto(txt, /(?:cobro|cobra|valor|r\$|reais?)?\s*(\d{2,4}(?:[.,]\d{1,2})?)\s*(?:reais?)?\s*(?:a|por|na|cada)\s*aula/i)
      ?? numeroPerto(txt, /(?:cobro|cobra|valor\s*(?:da|de)\s*aula\s*(?:e|é|de)?)\s*(?:r\$)?\s*(\d{2,4})/i)
      ?? numeroPerto(txt, /r\$\s*(\d{2,4})/i);
}

// "pago 30 de quadra", "sala custa 40", "30 de academia"
export function extrairCustoSala(txt) {
  const t = so(txt);
  if (/(nao pago|sem custo|nao tem custo|nao pago sala|nao pago quadra)/.test(t)) return 0;
  return numeroPerto(txt, /(?:pago|custa|custo\s*(?:de)?)\s*(?:r\$)?\s*(\d{1,4})\s*(?:reais?)?\s*(?:de|na|por|pela)?\s*(?:quadra|sala|academia|espa(ç|c)o)/i)
      ?? numeroPerto(txt, /(?:quadra|sala|academia)\s*(?:custa|sai|de|por)?\s*(?:r\$)?\s*(\d{1,4})/i);
}

// "pacote de 8", "8 aulas no pacote", "pacote 12"
export function extrairPacote(txt) {
  return numeroPerto(txt, /pacote\s*(?:de|com)?\s*(\d{1,3}|quatro|oito|doze|dezesseis|vinte)/i)
      ?? numeroPerto(txt, /(\d{1,3})\s*aulas?\s*(?:no|por|do)?\s*pacote/i);
}

// "já fez 3", "fez 3 aulas", "3 aulas realizadas", "já teve 2"
export function extrairJaFeitas(txt) {
  return numeroPerto(txt, /(?:j[áa]\s*)?(?:fez|teve|fizemos|deu|realizou)\s*(\d{1,3}|uma?|dois|duas|tr[êe]s|quatro|cinco|seis|sete|oito|nove|dez)/i)
      ?? numeroPerto(txt, /(\d{1,3})\s*aulas?\s*(?:j[áa]\s*)?(?:feitas?|realizadas?|dadas?)/i);
}

// pago / não pago
export function extrairPago(txt) {
  const t = so(txt);
  if (/(nao pagou|nao pago|nao esta pago|ta devendo|esta devendo|deve o pacote|em aberto|nao quitou)/.test(t)) return false;
  if (/(ja pagou|ta pago|esta pago|pagou|quitado|quitou|pago adiantado|pagou tudo)/.test(t)) return true;
  return null;
}

export function extrairTelefone(txt) {
  const m = String(txt).replace(/\D/g, '');
  return m.length >= 10 && m.length <= 13 ? m : null;
}

// O nome é o que sobra: normalmente vem primeiro, antes da primeira vírgula ou
// antes de qualquer palavra "de negócio". Fala natural começa pelo aluno.
export function extrairNome(txt) {
  let inicio = String(txt).split(/[,;]|\b(?:treina|faz|vem|joga|segunda|ter[çc]a|quarta|quinta|sexta|s[áa]bado|domingo|toda|todas|cobro|pacote)\b/i)[0] || '';
  // tira os prefixos em cadeia: "Cadastrar a Ana" / "Novo aluno Roberto" → "Ana" / "Roberto"
  const PREFIXOS = /^\s*(?:o|a|os|as|um|uma|meu|minha|novo|nova|aluno|aluna|cadastra(?:r)?|adiciona(?:r)?|inclui(?:r)?|cria(?:r)?|coloca(?:r)?|contrato|do|da|de|para|pro|pra)\s+/i;
  let antes;
  do { antes = inicio; inicio = inicio.replace(PREFIXOS, ''); } while (inicio !== antes);
  inicio = inicio.replace(/[^\p{L}\s'’-]/gu, '').trim();
  const palavras = inicio.split(/\s+/).filter(Boolean).slice(0, 3);
  if (!palavras.length) return '';
  return palavras.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
}

/**
 * Interpreta a frase falada e devolve os campos que conseguiu entender,
 * junto com a lista do que entendeu (para mostrar ao professor) e a confiança.
 */
export function interpretarContrato(texto) {
  const t = String(texto || '').trim();
  if (!t) return { dados: {}, entendido: [], texto: '' };

  const dias = extrairDias(t);
  const freq = extrairFrequencia(t);
  const horarios = extrairHorarios(t);
  const hora = extrairHora(t);
  const valor = extrairValorAula(t);
  const custo = extrairCustoSala(t);
  const pacote = extrairPacote(t);
  const feitas = extrairJaFeitas(t);
  const pago = extrairPago(t);
  const nome = extrairNome(t);

  const dados = {};
  const entendido = [];

  if (nome) { dados.nome = nome; entendido.push(`Aluno: ${nome}`); }
  if (valor) { dados.valor_aula = valor; entendido.push(`R$ ${valor} por aula`); }
  if (custo !== null && custo !== undefined) { dados.custo_sala = custo; entendido.push(custo ? `R$ ${custo} de sala` : 'sem custo de sala'); }
  if (dias.length) {
    dados.dias = dias;
    entendido.push(`Dias: ${dias.map(d => ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'][d]).join(', ')}`);
  } else if (freq) {
    dados.aulas_por_semana = freq;
    entendido.push(`${freq}x por semana`);
  }
  if (horarios) {
    dados.horarios = horarios;
    dados.hora = horarios[Object.keys(horarios)[0]];
    entendido.push('Horários: ' + Object.keys(horarios)
      .map(Number).sort((a, b) => a - b)
      .map(d => `${['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'][d]} ${horarios[d]}`).join(', '));
  } else if (hora) { dados.hora = hora; entendido.push(`Horário: ${hora}`); }
  if (pacote) { dados.pacote = pacote; entendido.push(`Pacote de ${pacote} aulas`); }
  if (feitas) {
    dados.em_curso = true; dados.aulas_feitas = feitas;
    entendido.push(`${feitas} aulas já realizadas`);
  }
  if (pago !== null) {
    dados.em_curso = dados.em_curso ?? true;
    dados.pacote_pago = pago;
    entendido.push(pago ? 'pacote já pago' : 'pacote em aberto');
  }

  return { dados, entendido, texto: t };
}
